import React from 'react';
import {Box, BoxProps, Text} from '@chakra-ui/react';
import {MinSamDefinitionSize} from '../../shared/api/api';
import {GuidanceBar, GuidanceBarMode} from '../GuidanceBar';
import {useSamEditorStore} from './useSamEditorStore';
import {useDebouncedSamSize, useSamDefinitionLimits} from './helpers';
import {
  useIsFreemiumCustomer,
  useIsFreeOrListBuilderCustomer,
} from '../../hooks/api/metadata';

interface SamGuidanceMessageProps extends BoxProps {
  defaultTransparentBg: boolean;
}

export const SamGuidanceMessage = ({
  defaultTransparentBg,
  ...rest
}: SamGuidanceMessageProps) => {
  const isFreemiumOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();
  const defaultMode = defaultTransparentBg ? 'default' : 'success';

  if (isFreemiumOrListBuilderCustomer) {
    return <SamLimitsMessage defaultMode={defaultMode} {...rest} />;
  } else {
    return <SamSizeMessage defaultMode={defaultMode} {...rest} />;
  }
};

const SamLimitsMessage = ({
  defaultMode,
  ...props
}: BoxProps & {defaultMode: GuidanceBarMode}) => {
  const {hasInitialized, isEmpty} = useSamEditorStore();
  const {isFetching, withinLimits} = useSamDefinitionLimits();
  const isFreemiumCustomer = useIsFreemiumCustomer();

  let mode: GuidanceBarMode = defaultMode;
  let samSizeHelperText: React.ReactNode;

  if (isFreemiumCustomer) {
    if (isFetching || withinLimits === undefined) {
      samSizeHelperText = 'Checking list size...';
    } else if (hasInitialized && isEmpty()) {
      samSizeHelperText = 'Set your SAM filters to continue.';
    } else if (!withinLimits) {
      mode = 'warning';
      samSizeHelperText = (
        <>
          Less than
          <Text as="b" mx="1">
            {MinSamDefinitionSize.toLocaleString()}
          </Text>
          accounts matched. Expand your filters to continue.
        </>
      );
    } else {
      samSizeHelperText = "That's a nice size list!";
    }
  } else {
    if (hasInitialized && isEmpty()) {
      samSizeHelperText = 'Set your SAM filters to continue.';
    } else {
      return <></>;
    }
  }

  return (
    <GuidanceBar mode={mode} {...props}>
      <Box>{samSizeHelperText}</Box>
    </GuidanceBar>
  );
};

const SamSizeMessage = ({
  defaultMode,
  ...props
}: BoxProps & {defaultMode: GuidanceBarMode}) => {
  const {size, isFetching} = useDebouncedSamSize();

  let mode: GuidanceBarMode = defaultMode;
  let samSizeHelperText: React.ReactNode;

  if (isFetching || size === undefined) {
    samSizeHelperText = 'Checking list size...';
  } else if (size === 0) {
    mode = 'warning';
    samSizeHelperText = 'No accounts matched';
  } else {
    samSizeHelperText = (
      <>
        <Text as="b" mx="1">
          {size.toLocaleString()}
        </Text>
        accounts matched.
      </>
    );
  }

  return (
    <GuidanceBar mode={mode} {...props}>
      <Box>{samSizeHelperText}</Box>
    </GuidanceBar>
  );
};
